import { takeWhile } from 'rxjs/operators';
import { Store } from '@ngrx/store';
// Utils / Constants
import equal from 'NewAngular/utils/equal/deep-equal';
import { SCATTER_PLOT_INTERACTIVE_PARAMS_NO_NEW_DATA } from 'ChartApp/ts/apps/scatter-plot/constants';
import { YChartsSentryUtils } from 'NewAngular/services/sentry/sentry-utils';
// NgRx actions / selectors
import { setAxisExtremes } from 'ChartApp/ts/apps/scatter-plot/state/axis-extremes/axis-extremes.action';
// NgRx store actions / selectors
import { getScatterPlotFetchParams, hasValidRequestData, getPlotOptions, } from 'ChartApp/ts/apps/scatter-plot/state/scatter-plot.selectors';
import { setScatterPlot } from 'ChartApp/ts/apps/scatter-plot/state/scatter-plot.actions';
import { ScatterPlotStatus } from 'ChartApp/ts/enums/scatter-plot-common.enum';
// Components
import { LegacyAssetConversionCheckerComponent } from 'ConverterApp/features/legacy-asset-conversion-checker/legacy-asset-conversion-checker.component';
// Services
import { ChartZoomService } from 'ChartApp/ts/features/charts-shared/services/chart-zoom.service';
import { SavedScatterPlotService } from 'NewAngular/features/components/saved-items/services/saved-scatter-plot.service';
import { ScatterPlotService } from 'ChartApp/ts/services/scatter-plot.service';
import { SelectedScatterPlotService } from 'ChartApp/ts/apps/scatter-plot/services/selected-scatter-plot.service';
// Directives
import { ScatterPlotBaseDirective } from 'ChartApp/ts/features/scatter-plot/directives/scatter-plot-base.directive';
import * as i0 from "@angular/core";
import * as i1 from "NewAngular/features/components/saved-items/services/saved-scatter-plot.service";
import * as i2 from "@ngrx/store";
import * as i3 from "ChartApp/ts/services/scatter-plot.service";
import * as i4 from "ChartApp/ts/apps/scatter-plot/services/selected-scatter-plot.service";
import * as i5 from "ChartApp/ts/features/charts-shared/services/chart-zoom.service";
import * as i6 from "../../../../../../../../converter/static/converter/ts/features/legacy-asset-conversion-checker/legacy-asset-conversion-checker.component";
import * as i7 from "../../../scatter-plot/components/scatter-plot/scatter-plot.component";
import * as i8 from "../../../scatter-plot/components/scatter-plot-legend/scatter-plot-legend.component";
/**
 *
 * Scatter Plot Chart Interactive
 *
 * This displays the interactive chart and legend.
 *
 */
export class ScatterPlotChartInteractiveComponent extends ScatterPlotBaseDirective {
    constructor(savedScatterPlotService, store, scatterPlotService, selectedScatterPlotService, chartZoomService) {
        super();
        this.savedScatterPlotService = savedScatterPlotService;
        this.store = store;
        this.scatterPlotService = scatterPlotService;
        this.selectedScatterPlotService = selectedScatterPlotService;
        this.chartZoomService = chartZoomService;
        // Fetching Data
        this.isAlive = true;
        // Defined as an arrow function so that this refers to the component
        this.mapStateToComponent = (state) => {
            // Avoid displaying data and errors when user is not logged in
            if (!window.USER.authenticated) {
                return;
            }
            this.plotOptions = getPlotOptions(state);
            // Determine if we need to fetch a new scatter plot data based on changes
            // to the filter parameters.
            const currentDataParams = this.getDataParams;
            const newDataParams = getScatterPlotFetchParams(state);
            if (this.fetchParamsChanged(currentDataParams, newDataParams)) {
                this.getDataParams = newDataParams;
                if (hasValidRequestData(state)) {
                    // Clear possible errors saved from the previous request
                    this.error = { code: '', message: '' };
                    // Check to see if there is already a fetch pending. If there is we need to
                    // "cancel" the request by unsubscribing from it before creating a new subscription.
                    if (this.fetchSubscription !== null) {
                        this.fetchSubscription.unsubscribe();
                    }
                    // Make a request to load scatter plot data
                    this.fetchSubscription = this.handleFetch();
                    if (this.fetchSubscription !== null) {
                        this.fetchSubscription.add(() => {
                            const chartId = this.getDataParams.chartId;
                            if (chartId !== null && chartId !== '') {
                                this.legacyConverter.assetId = chartId;
                                this.legacyConverter.reloadDiffAndCheck();
                            }
                        });
                    }
                }
            }
            if (!hasValidRequestData(state)) {
                this.status = ScatterPlotStatus.getStarted;
            }
        };
        this.store
            .select('reducers')
            .pipe(takeWhile(() => this.isAlive))
            .subscribe(this.mapStateToComponent);
        this.axisExtremesSubscription = this.chartZoomService
            .getAxisExtremes()
            .subscribe((axisExtremes) => {
            this.store.dispatch(setAxisExtremes({ axisExtremes }));
        });
    }
    ngOnDestroy() {
        super.ngOnDestroy();
        this.isAlive = false;
    }
    fetchParamsChanged(currentDataParams, newDataParams) {
        const _currentDataParams = { ...currentDataParams };
        const _newDataParams = { ...newDataParams };
        // Remove params that don't require a request for new data
        SCATTER_PLOT_INTERACTIVE_PARAMS_NO_NEW_DATA.forEach((param) => {
            delete _currentDataParams[param];
            delete _newDataParams[param];
        });
        return !equal(_currentDataParams, _newDataParams);
    }
    handleFetch() {
        const scatterPlotDataObservable$ = this.scatterPlotService.getScatterPlotData(this.getDataParams);
        // Only make a request if data can be returned for the get params
        if (scatterPlotDataObservable$) {
            this.status = ScatterPlotStatus.loading;
            return scatterPlotDataObservable$.subscribe({
                next: (rsp) => {
                    this.scatterPlotInfo = { ...rsp };
                    // Filter data for series within the data limit
                    const seriesWithinDataLimit = this.ychartsScatterPlotService.filterSeriesWithTooManyPoints(this.scatterPlotInfo.data.series);
                    this.scatterPlotInfo.data.series = seriesWithinDataLimit;
                    if (seriesWithinDataLimit.length === 0) {
                        this.status = ScatterPlotStatus.allSeriesExceedDataLimit;
                    }
                    else if (!this.ychartsScatterPlotService.seriesDataExists(rsp.data.series)) {
                        this.status = ScatterPlotStatus.noData;
                    }
                    else {
                        this.status = ScatterPlotStatus.ok;
                    }
                    this.updateStateBasedOnDataFetched(rsp);
                },
                error: (error) => {
                    this.status = ScatterPlotStatus.error;
                    this.error = error.error;
                    // Only capture unexpected exceptions
                    if (!this.backendErrorCodes.has(this.error.code)) {
                        YChartsSentryUtils.captureException(error);
                    }
                },
            });
        }
        // If data could not be requested, then the chart is considered "unstarted"
        this.status = ScatterPlotStatus.getStarted;
        return null;
    }
    handleAssetChecked(updated) {
        if (updated && this.getDataParams.chartId !== null) {
            this.selectedScatterPlotService.selectFolderable(this.getDataParams.chartId);
        }
    }
    handleAssetCopied(chartId) {
        this.selectedScatterPlotService.selectFolderable(parseInt(chartId, 10));
    }
    updateStateBasedOnDataFetched(rsp) {
        this.store.dispatch(setScatterPlot({ scatterPlot: rsp.params }));
    }
}
ScatterPlotChartInteractiveComponent.ɵfac = function ScatterPlotChartInteractiveComponent_Factory(__ngFactoryType__) { return new (__ngFactoryType__ || ScatterPlotChartInteractiveComponent)(i0.ɵɵdirectiveInject(i1.SavedScatterPlotService), i0.ɵɵdirectiveInject(i2.Store), i0.ɵɵdirectiveInject(i3.ScatterPlotService), i0.ɵɵdirectiveInject(i4.SelectedScatterPlotService), i0.ɵɵdirectiveInject(i5.ChartZoomService)); };
ScatterPlotChartInteractiveComponent.ɵcmp = /*@__PURE__*/ i0.ɵɵdefineComponent({ type: ScatterPlotChartInteractiveComponent, selectors: [["ycn-scatter-plot-chart-interactive"]], viewQuery: function ScatterPlotChartInteractiveComponent_Query(rf, ctx) { if (rf & 1) {
        i0.ɵɵviewQuery(LegacyAssetConversionCheckerComponent, 5);
    } if (rf & 2) {
        let _t;
        i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.legacyConverter = _t.first);
    } }, standalone: false, features: [i0.ɵɵProvidersFeature([ChartZoomService]), i0.ɵɵInheritDefinitionFeature], decls: 7, vars: 8, consts: [[1, "chart-interactive", "full-height"], [1, "chart-item", "full-height"], [2, "flex-shrink", "0", "width", "100%", 3, "scatterPlotInfo", "status", "plotOptions"], [1, "chart-wrap", "full-height"], [1, "full-height"], [1, "chart-canvas", "d-flex", "flex-column", 3, "errorMessage", "scatterPlotInfo", "status", "ychartsScatterPlotService"], [3, "assetChecked", "assetCopied", "assetType"]], template: function ScatterPlotChartInteractiveComponent_Template(rf, ctx) { if (rf & 1) {
        i0.ɵɵelementStart(0, "div", 0)(1, "div", 1);
        i0.ɵɵelement(2, "ycn-scatter-plot-legend", 2);
        i0.ɵɵelementStart(3, "div", 3)(4, "div", 4);
        i0.ɵɵelement(5, "ycn-scatter-plot", 5);
        i0.ɵɵelementEnd()()()();
        i0.ɵɵelementStart(6, "ycn-legacy-asset-conversion-checker", 6);
        i0.ɵɵlistener("assetChecked", function ScatterPlotChartInteractiveComponent_Template_ycn_legacy_asset_conversion_checker_assetChecked_6_listener($event) { return ctx.handleAssetChecked($event); })("assetCopied", function ScatterPlotChartInteractiveComponent_Template_ycn_legacy_asset_conversion_checker_assetCopied_6_listener($event) { return ctx.handleAssetCopied($event); });
        i0.ɵɵelementEnd();
    } if (rf & 2) {
        i0.ɵɵadvance(2);
        i0.ɵɵproperty("scatterPlotInfo", ctx.scatterPlotInfo)("status", ctx.status)("plotOptions", ctx.plotOptions);
        i0.ɵɵadvance(3);
        i0.ɵɵproperty("errorMessage", ctx.error.message)("scatterPlotInfo", ctx.scatterPlotInfo)("status", ctx.status)("ychartsScatterPlotService", ctx.ychartsScatterPlotService);
        i0.ɵɵadvance();
        i0.ɵɵproperty("assetType", "scatter_plot");
    } }, dependencies: [i6.LegacyAssetConversionCheckerComponent, i7.ScatterPlotComponent, i8.ScatterPlotLegendComponent], encapsulation: 2 });
